import { useLocation, matchPath } from "react-router";
import { useMemo } from "react";
import {
  publicRoutes,
  protectedRoutes,
  widgetPath,
  loadFinderPath,
  batchPath,
  brokerAnalyticsPath,
  trucklistPath,
  protectedPaths,
} from "@/routing";

export const useIsProtectedRoute = () => {
  const location = useLocation();
  // matchPath so slug-based public routes (e.g. /trucklist/:slug) are recognised as public
  return useMemo(() => !publicRoutes.some((route) => matchPath(route.path, location.pathname)), [location.pathname]);
};

export const useIsTrucklistRoute = () => {
  const location = useLocation();
  return useMemo(() => !!matchPath(trucklistPath, location.pathname), [location.pathname]);
};

export const useIsDataRoute = () => {
  const location = useLocation();
  return useMemo(
    () =>
      [protectedRoutes[1], protectedRoutes[2], protectedRoutes[6], protectedRoutes[8]].some((route) => route.path === location.pathname) ||
      location.pathname === widgetPath,
    [location.pathname],
  );
};

export const useIsSearchRoute = () => {
  const location = useLocation();
  return useMemo(
    () =>
      [
        protectedRoutes[0],
        protectedRoutes[1],
        protectedRoutes[2],
        protectedRoutes[4],
        protectedRoutes[6],
        protectedRoutes[7],
        protectedRoutes[8],
        protectedRoutes[9],
        protectedRoutes[10],
      ].some((route) => route.path === location.pathname) || location.pathname === widgetPath,
    [location.pathname],
  );
};

export const useIsFeedbackRoute = () => {
  const location = useLocation();
  return useMemo(
    () =>
      [protectedRoutes[0], protectedRoutes[1], protectedRoutes[2], protectedRoutes[6], protectedRoutes[8]].some(
        (route) => route.path === location.pathname,
      ) || location.pathname === widgetPath,
    [location.pathname],
  );
};

export const useIsPublicRoute = () => {
  const location = useLocation();
  return useMemo(() => publicRoutes.some((route) => route.path === location.pathname), [location.pathname]);
};

export const useIsLoginRoute = () => {
  const location = useLocation();
  return useMemo(() => [publicRoutes[0], publicRoutes[1]].some((route) => route.path === location.pathname), [location.pathname]);
};

export const useReturnUrl = () => {
  const location = useLocation();
  return encodeURIComponent(`${location.pathname.slice(1)}${location.search}`);
};

export const useLocationPotentialRouteState = () => {
  const location = useLocation();
  return useMemo(
    () =>
      protectedRoutes[1].path === location.pathname
        ? "load_rates"
        : protectedRoutes[2].path === location.pathname
          ? "market_potential"
          : protectedRoutes[0].path === location.pathname
            ? "home"
            : protectedRoutes[4].path === location.pathname
              ? "company_analytics"
              : protectedRoutes[6].path === location.pathname || location.pathname === widgetPath
                ? "loadboard"
                : protectedRoutes[8].path === location.pathname
                  ? "load_finder"
                  : protectedRoutes[9].path === location.pathname
                    ? "batch"
                    : protectedRoutes[10].path === location.pathname
                      ? "broker_analytics"
                      : undefined,
    [location.pathname],
  );
};

export const useIsAnalyticRoute = () => {
  const location = useLocation();
  return useMemo(() => protectedRoutes[4].path === location.pathname, [location.pathname]);
};

export const useIsLoadSearchRoute = () => {
  const location = useLocation();
  return useMemo(() => protectedRoutes[6].path === location.pathname || location.pathname === widgetPath, [location.pathname]);
};

export const useIsCircleRoute = () => {
  const location = useLocation();
  return useMemo(
    () => [publicRoutes[0], publicRoutes[1], protectedRoutes[3]].some((route) => route.path === location.pathname),
    [location.pathname],
  );
};

export const useIsWidgetRoute = () => {
  const location = useLocation();
  return useMemo(() => location.pathname === widgetPath, [location.pathname]);
};

export const useIsLoadFinderRoute = () => {
  const location = useLocation();
  return useMemo(() => location.pathname === loadFinderPath, [location.pathname]);
};

export const useIsChatAssistantRoute = () => {
  const location = useLocation();
  return useMemo(
    () => protectedPaths.some((path) => matchPath(path, location.pathname)) && location.pathname !== widgetPath,
    [location.pathname],
  );
};

export const useIsBatchRoute = () => {
  const location = useLocation();
  return useMemo(() => location.pathname === batchPath, [location.pathname]);
};

export const useIsBrokerAnalyticsRoute = () => {
  const location = useLocation();
  return useMemo(() => location.pathname === brokerAnalyticsPath, [location.pathname]);
};
